import React, { useState, useEffect } from 'react';
import { XMarkIcon, CheckCircleIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { UserCircleIcon } from '@heroicons/react/24/solid';
import websocketService from '../../utils/websocketService';

const InvitationAcceptedNotification = ({ user }) => {
  const [notifications, setNotifications] = useState([]);

  // Auto-remove notification after 10 seconds
  const removeNotification = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  // Add auto-removal timeout when notification is added
  const addNotification = (notification) => {
    const notificationWithId = {
      ...notification,
      id: Date.now() + Math.random(), // Simple unique ID
      timestamp: new Date()
    };
    
    setNotifications(prev => [notificationWithId, ...prev]);
    
    // Auto-remove after 10 seconds
    setTimeout(() => {
      removeNotification(notificationWithId.id);
    }, 10000);
    
    return notificationWithId.id;
  };

  // WebSocket message handler
  const handleWebSocketMessage = (data) => {
    console.log('🎯 InvitationAcceptedNotification received message:', data.type, data);
    console.log('📦 Full message data:', JSON.stringify(data, null, 2));
    
    if (data.type === 'project_invitation_accepted') {
      console.log('✨ Processing invitation accepted notification:', data.data);
      addNotification(data.data);
      
      // Trigger projects update event to refresh sidebar
      console.log('🔄 Triggering projectsUpdated event');
      window.dispatchEvent(new CustomEvent('projectsUpdated'));
    } else {
      console.log('🔍 Message type not handled by InvitationAcceptedNotification:', data.type);
    }
  };

  // WebSocket connection and cleanup - only when fully authenticated
  useEffect(() => {
    console.log('🚀 InvitationAcceptedNotification useEffect - user:', user?.id);
    
    if (!user?.id) {
      console.log('❌ No user ID, skipping WebSocket setup');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      console.log('❌ No token found, skipping WebSocket setup');
      return;
    }

    // Don't connect during onboarding or when not authenticated
    if (!user.isOnboarded) {
      console.log('❌ User not onboarded, skipping WebSocket setup');
      return;
    }

    const listenerId = 'invitation-accepted';
    console.log(`➕ Setting up WebSocket listener: ${listenerId} for user: ${user.id}`);
    
    // Connect to shared WebSocket service
    websocketService.connect(token);
    websocketService.addListener(listenerId, handleWebSocketMessage);

    return () => {
      console.log(`➖ Cleaning up WebSocket listener: ${listenerId}`);
      websocketService.removeListener(listenerId);
    };
  }, [user?.id]);

  // Debug component mount/unmount
  useEffect(() => {
    console.log('🔗 InvitationAcceptedNotification component mounted for user:', user?.id);
    
    // Debug WebSocket connection status every 5 seconds
    // const debugInterval = setInterval(() => {
    //   const connectionState = websocketService.getConnectionState();
    //   const isConnected = websocketService.isConnected();
    //   console.log('🔌 InvitationAcceptedNotification WebSocket status:', {
    //     connectionState,
    //     isConnected,
    //     userId: user?.id
    //   });
    // }, 5000);
    
    return () => {
      console.log('🔌 InvitationAcceptedNotification component unmounting');
      // clearInterval(debugInterval);
    };
  }, []);

  // Format relative time
  const formatTimeAgo = (date) => {
    const diffInMs = new Date() - date;
    const diffInSeconds = Math.floor(diffInMs / 1000);
    
    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      return `${Math.floor(diffInSeconds / 60)}m ago`;
    } else {
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    }
  };

  // Don't render if no user or no notifications
  if (!user?.id || notifications.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[9998] space-y-2">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          className="w-96 bg-white dark:bg-[#181818] rounded-2xl shadow-2xl border border-gray-200 dark:border-[#444444] animate-in slide-in-from-right duration-300"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-[#444444] bg-green-50 dark:bg-green-900/20">
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
              <h3 className="text-sm font-semibold text-green-800 dark:text-green-200">
                Invitation Accepted
              </h3>
            </div>
            <button
              onClick={() => removeNotification(notification.id)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 rounded-md hover:bg-gray-200 dark:hover:bg-[#333] transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4">
            <div className="flex items-start gap-3">
              {notification.acceptedBy.profileImage?.url ? (
                <img
                  src={notification.acceptedBy.profileImage.url}
                  alt={notification.acceptedBy.name}
                  className="w-10 h-10 rounded-full flex-shrink-0"
                />
              ) : (
                <UserCircleIcon className="w-10 h-10 text-gray-400 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 dark:text-white">
                  <span className="font-medium">{notification.acceptedBy.name}</span> accepted your invitation to join
                </p>
                <h4 className="text-base font-semibold text-gray-900 dark:text-white truncate mt-1">
                  {notification.project.name}
                </h4>
                
                {/* Project info */}
                <div className="flex items-center mt-2 text-xs text-gray-500 dark:text-gray-400">
                  <UserGroupIcon className="w-4 h-4 mr-1" />
                  <span>
                    {notification.project.memberCount} member{notification.project.memberCount !== 1 ? 's' : ''}
                  </span>
                </div>
                
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  {formatTimeAgo(notification.timestamp)}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default InvitationAcceptedNotification; 